import { supabase } from '../lib/supabase';

export const affiliateService = {
  // Get affiliate earnings by user ID
  async getEarnings(userId) {
    const { data, error } = await supabase?.from('affiliate_earnings')?.select('*')?.eq('user_id', userId)?.single();
    
    if (error) {
      // Handle table not found error gracefully
      if (error?.code === 'PGRST205') {
        console.warn('Affiliate earnings table not found. Please apply Supabase migration.');
        return {
          id: null,
          userId: userId,
          totalEarnings: 0,
          totalWithdrawn: 0,
          totalPoints: 0,
          level1Earnings: 0,
          level2Earnings: 0,
          totalReferrals: 0,
          withdrawalEligible: false,
          createdAt: null,
          updatedAt: null
        };
      }
      
      if (error?.code === 'PGRST116') {
        // No earnings record, create one
        return await this.createEarningsRecord(userId);
      }
      throw error;
    }
    
    return {
      id: data?.id,
      userId: data?.user_id,
      totalEarnings: parseFloat(data?.total_earnings || 0),
      totalWithdrawn: parseFloat(data?.total_withdrawn || 0),
      totalPoints: data?.total_points || 0,
      level1Earnings: parseFloat(data?.level1_earnings || 0),
      level2Earnings: parseFloat(data?.level2_earnings || 0),
      totalReferrals: data?.total_referrals || 0,
      withdrawalEligible: data?.withdrawal_eligible || false,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Create earnings record
  async createEarningsRecord(userId) {
    const { data, error } = await supabase?.from('affiliate_earnings')?.insert({ user_id: userId })?.select()?.single();
    
    if (error) {
      // Handle table not found error gracefully
      if (error?.code === 'PGRST205') {
        console.warn('Affiliate earnings table not found. Cannot create record.');
        return {
          id: null,
          userId: userId,
          totalEarnings: 0,
          totalWithdrawn: 0,
          totalPoints: 0,
          level1Earnings: 0,
          level2Earnings: 0,
          totalReferrals: 0,
          withdrawalEligible: false
        };
      }
      throw error;
    }
    
    return {
      id: data?.id,
      userId: data?.user_id,
      totalEarnings: 0,
      totalWithdrawn: 0,
      totalPoints: 0,
      level1Earnings: 0,
      level2Earnings: 0,
      totalReferrals: 0,
      withdrawalEligible: false
    };
  },

  // Get withdrawal requests
  async getWithdrawalRequests(userId) {
    const { data, error } = await supabase?.from('withdrawal_requests')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
    
    if (error) {
      // Handle table not found error gracefully
      if (error?.code === 'PGRST205') {
        console.warn('Withdrawal requests table not found. Please apply Supabase migration.');
        return [];
      }
      throw error;
    }
    
    return data?.map(req => ({
      id: req?.id,
      userId: req?.user_id,
      withdrawalCode: req?.withdrawal_code,
      amount: parseFloat(req?.amount),
      paymentMethod: req?.payment_method,
      accountNumber: req?.account_number,
      accountHolderName: req?.account_holder_name,
      status: req?.status,
      adminObservation: req?.admin_observation,
      adminRecommendation: req?.admin_recommendation,
      processedBy: req?.processed_by,
      processedAt: req?.processed_at,
      createdAt: req?.created_at
    }));
  },

  // Create withdrawal request
  async createWithdrawalRequest(userId, requestData) {
    const { data, error } = await supabase?.from('withdrawal_requests')?.insert({
        user_id: userId,
        amount: requestData?.amount,
        payment_method: requestData?.paymentMethod,
        account_number: requestData?.accountNumber,
        account_holder_name: requestData?.accountHolderName
      })?.select()?.single();
    
    if (error) {
      // Handle table not found error gracefully
      if (error?.code === 'PGRST205') {
        console.warn('Withdrawal requests table not found. Cannot create request.');
        throw new Error('La funcionalidad de retiros no está disponible. Por favor, contacte al administrador.');
      }
      throw error;
    }
    
    return {
      id: data?.id,
      withdrawalCode: data?.withdrawal_code,
      amount: parseFloat(data?.amount),
      status: data?.status,
      createdAt: data?.created_at
    };
  }
};
